import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  ChevronDown,
  LogOut,
  Mail,
  Menu,
  Moon,
  Phone,
  Sun,
  User,
  UserCog,
} from "lucide-react";
import api from "../api/api";
import NotificacoesDropdown from "./NotificacoesDropdown";
import { BRAND_LOGO_MARK_URL, buildUploadUrl } from "../utils/runtimeConfig";
import { sessionManager } from "../utils/sessionManager";

const getFotoSrc = (foto) => {
  if (!foto || typeof foto !== "string") return "";
  if (foto.startsWith("data:") || foto.startsWith("blob:") || foto.startsWith("http")) return foto;
  return buildUploadUrl(foto);
};

const roleInfo = {
  super_admin: { label: "Administrador geral", className: "role-admin" },
  admin: { label: "Administrador", className: "role-admin" },
  profissional: { label: "Profissional", className: "role-professional" },
  cliente: { label: "Cliente", className: "role-client" },
};

const getRoleInfo = (tipo) =>
  roleInfo[String(tipo || "").toLowerCase()] || { label: "Tipo não informado", className: "role-muted" };

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDark, setIsDark] = React.useState(document.body.classList.contains("dark"));
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [usuario, setUsuario] = React.useState(null);
  const [estabelecimentoNome, setEstabelecimentoNome] = React.useState("");
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  React.useEffect(() => {
    async function fetchUsuario() {
      try {
        const res = await api.get("/usuarios/me");
        setUsuario(res.data);

        if (res.data?.nome) {
          localStorage.setItem("userName", res.data.nome);
        }

        if (res.data?.estabelecimento?.nome) {
          setEstabelecimentoNome(res.data.estabelecimento.nome);
          localStorage.setItem("estabelecimento_id", String(res.data.estabelecimento.id));
          localStorage.setItem("estabelecimentoNome", res.data.estabelecimento.nome);
        } else if (res.data?.estabelecimento_id) {
          try {
            const { data: est } = await api.get(`/estabelecimentos/${res.data.estabelecimento_id}`);
            if (est?.nome) {
              setEstabelecimentoNome(est.nome);
              localStorage.setItem("estabelecimento_id", String(est.id));
              localStorage.setItem("estabelecimentoNome", est.nome);
            }
          } catch {
            // Mantem o header utilizavel mesmo se o estabelecimento falhar.
          }
        }
      } catch (err) {
        console.error("Erro ao buscar usuário no Header:", err);
      }
    }

    fetchUsuario();
  }, []);

  React.useEffect(() => {
    async function fetchById(id) {
      try {
        const { data } = await api.get(`/estabelecimentos/${id}`);
        if (data?.nome) {
          setEstabelecimentoNome(data.nome);
          localStorage.setItem("estabelecimentoNome", data.nome);
          localStorage.setItem("estabelecimento_id", String(data.id));
        }
      } catch {
        // noop
      }
    }

    if (usuario?.estabelecimento?.nome) {
      setEstabelecimentoNome(usuario.estabelecimento.nome);
      localStorage.setItem("estabelecimentoNome", usuario.estabelecimento.nome);
      localStorage.setItem("estabelecimento_id", String(usuario.estabelecimento.id));
      return;
    }

    if (usuario?.estabelecimento_id) {
      fetchById(usuario.estabelecimento_id);
    }
  }, [usuario]);

  React.useEffect(() => {
    function handleDocClick(e) {
      if (!document.body.classList.contains("sidebar-open")) return;
      const target = e.target;
      if (target.closest?.(".sidebar")) return;
      if (target.closest?.(".mobile-hamburger")) return;
      document.body.classList.remove("sidebar-open");
    }

    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
  }, []);

  React.useEffect(() => {
    if (!menuOpen) return;

    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const toggleTheme = () => {
    document.body.classList.toggle("dark");
    const theme = document.body.classList.contains("dark") ? "dark" : "light";
    localStorage.setItem("theme", theme);
    setIsDark(theme === "dark");
  };

  const toggleSidebarMobile = () => {
    document.body.classList.toggle("sidebar-open");
  };

  const handleLogout = async () => {
    await sessionManager.encerrarSessaoAtualSeExistir();
    navigate("/login", { replace: true });
  };

  const handleEditPerfil = () => {
    setMenuOpen(false);
    navigate("/perfil");
  };

  const fotoSrc = getFotoSrc(usuario?.foto);
  const tipoInfo = getRoleInfo(usuario?.tipo);
  const nomeUsuario = usuario?.nome || localStorage.getItem("userName") || "";
  const inicialUsuario = String(nomeUsuario || "U")
    .trim()
    .charAt(0)
    .toLocaleUpperCase("pt-BR");

  return (
    <header className="header">
      <button className="mobile-hamburger" aria-label="Abrir menu" onClick={toggleSidebarMobile}>
        <Menu size={22} />
      </button>

      <div className="header__brand">
        <img src={BRAND_LOGO_MARK_URL} alt="" aria-hidden="true" />
        <h1 className="header__title">
          Painel Administrativo{estabelecimentoNome ? ` - ${estabelecimentoNome}` : ""}
        </h1>
      </div>

      <div className="header__user">
        <NotificacoesDropdown usuarioId={usuario?.id} />

        <button className="theme-toggle" onClick={toggleTheme} title="Alternar tema">
          {isDark ? <Sun /> : <Moon />}
        </button>

        <div className="account-menu" ref={menuRef}>
          <button
            type="button"
            className="account-button"
            onClick={() => setMenuOpen((open) => !open)}
            title="Abrir menu do usuário"
            aria-expanded={menuOpen}
          >
            <span className="account-button__avatar">
              {fotoSrc ? <img src={fotoSrc} alt="" /> : <User size={19} />}
            </span>
            <span className="account-button__name">{nomeUsuario}</span>
            <ChevronDown size={16} className={menuOpen ? "account-button__chevron active" : "account-button__chevron"} />
          </button>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className="user-menu-popover"
                initial={{ opacity: 0, scale: 0.98, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: -10 }}
                transition={{ duration: 0.22 }}
                style={{
                  position: isMobile ? "fixed" : "absolute",
                  top: isMobile ? 66 : 48,
                  right: isMobile ? 8 : 0,
                  left: isMobile ? 8 : "auto",
                  width: isMobile ? "min(340px, calc(100vw - 16px))" : 320,
                }}
              >
                <div className="user-menu-popover__profile">
                  <div className="user-menu-popover__avatar">
                    {fotoSrc ? <img src={fotoSrc} alt="" /> : <User size={28} />}
                  </div>
                  <div className="user-menu-popover__identity">
                    <strong>{nomeUsuario || "Usuário"}</strong>
                    {fotoSrc ? (
                      <img src={fotoSrc} alt="" />
                    ) : (
                      <span className="user-menu-popover__initial">{inicialUsuario}</span>
                    )}
                      <span>
                        <Mail size={14} />
                        {usuario.email}
                      </span>
                    )}
                    {usuario?.telefone && (
                      <span>
                        <Phone size={14} />
                        {usuario.telefone}
                      </span>
                    )}
                    {estabelecimentoNome && (
                      <span>
                        <Building2 size={14} />
                        {estabelecimentoNome}
                      </span>
                    )}
                    <span className={`user-role-badge ${tipoInfo.className}`}>{tipoInfo.label}</span>
                  </div>
                </div>

                <div className="user-menu-popover__actions">
                  <button type="button" className="user-menu-action" onClick={handleEditPerfil}>
                    <UserCog size={18} />
                    Editar perfil
                  </button>
                  <button type="button" className="user-menu-action user-menu-action--danger" onClick={handleLogout}>
                    <LogOut size={18} />
                    Sair do painel
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;
